import React, { useState, useEffect } from 'react';

const DraftPickerModal = ({ isOpen, onClose, onSave, title, currentPicks = [], availableTargets = {} }) => {
  const [picks, setPicks] = useState([]);

  useEffect(() => {
    if (isOpen) {
      // 存在しないIDを除外してセット
      setPicks(currentPicks.filter(id => availableTargets[id]));
    }
  }, [isOpen, currentPicks, availableTargets]);

  if (!isOpen) return null;

  const handleAdd = (id) => {
    if (!picks.includes(id)) {
      setPicks([...picks, id]);
    }
  };

  const handleRemove = (id) => {
    setPicks(picks.filter(p => p !== id));
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newPicks = [...picks];
    const temp = newPicks[index - 1];
    newPicks[index - 1] = newPicks[index];
    newPicks[index] = temp;
    setPicks(newPicks);
  };

  const handleMoveDown = (index) => {
    if (index === picks.length - 1) return;
    const newPicks = [...picks];
    const temp = newPicks[index + 1];
    newPicks[index + 1] = newPicks[index];
    newPicks[index] = temp;
    setPicks(newPicks);
  };

  const handleSave = () => {
    onSave(picks);
    onClose();
  };

  // 選択されていないターゲット
  const unpickedTargets = Object.entries(availableTargets).filter(([id]) => !picks.includes(id));

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h3 style={styles.title}>{title}</h3>
          <button style={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={styles.body}>
          <div style={styles.column}>
            <h4 style={styles.colTitle}>選択済み（上から希望順）</h4>
            <div style={styles.list}>
              {picks.length === 0 && <p style={styles.empty}>選択されていません</p>}
              {picks.map((id, index) => {
                const target = availableTargets[id];
                if (!target) return null;
                return (
                  <div key={id} style={styles.item}>
                    <span style={styles.rank}>{index + 1}位</span>
                    <span style={styles.itemName}>{target.name || target.vrchat_name}</span>
                    <div style={styles.actions}>
                      <button style={styles.iconBtn} onClick={() => handleMoveUp(index)}>↑</button>
                      <button style={styles.iconBtn} onClick={() => handleMoveDown(index)}>↓</button>
                      <button style={styles.removeBtn} onClick={() => handleRemove(id)}>削除</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={styles.column}>
            <h4 style={styles.colTitle}>未選択</h4>
            <div style={styles.list}>
              {unpickedTargets.length === 0 && <p style={styles.empty}>すべて選択済み</p>}
              {unpickedTargets.map(([id, target]) => (
                <div key={id} style={styles.item}>
                  <span style={styles.itemName}>{target.name || target.vrchat_name}</span>
                  <button style={styles.addBtn} onClick={() => handleAdd(id)}>追加</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose}>キャンセル</button>
          <button style={styles.saveBtn} onClick={handleSave}>保存</button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: '#fff',
    borderRadius: '12px',
    width: '600px',
    maxWidth: '90%',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
  },
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid #eee',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: { margin: 0, fontSize: '18px', color: '#333' },
  closeBtn: { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#999' },
  body: {
    padding: '20px',
    display: 'flex',
    gap: '20px',
    flex: 1,
    overflow: 'hidden'
  },
  column: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '1px solid #eee',
    borderRadius: '8px',
    background: '#f9f9f9'
  },
  colTitle: {
    margin: 0,
    padding: '10px',
    background: '#eee',
    fontSize: '14px',
    textAlign: 'center'
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 10px',
    background: '#fff',
    borderRadius: '6px',
    border: '1px solid #ddd'
  },
  rank: { fontWeight: 'bold', marginRight: '8px', color: '#6c63ff', fontSize: '14px' },
  itemName: { flex: 1, fontSize: '14px', fontWeight: '600' },
  actions: { display: 'flex', gap: '4px' },
  iconBtn: { padding: '4px 8px', border: '1px solid #ccc', background: '#fff', borderRadius: '4px', cursor: 'pointer' },
  addBtn: { padding: '4px 10px', border: 'none', background: '#e0e7ff', color: '#4338ca', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' },
  removeBtn: { padding: '4px 10px', border: 'none', background: '#fee2e2', color: '#991b1b', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' },
  empty: { textAlign: 'center', color: '#999', fontSize: '13px', margin: '20px 0' },
  footer: {
    padding: '16px 20px',
    borderTop: '1px solid #eee',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  },
  cancelBtn: { padding: '8px 16px', background: '#fff', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer' },
  saveBtn: { padding: '8px 16px', background: '#6c63ff', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }
};

export default DraftPickerModal;
